import React from 'react';
import {
  Box,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Flag as FlagIcon,
  School as EducationIcon,
  Work as WorkIcon,
  CardMembership as CertIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import {
  SnapshotTab,
  LicencesTab,
  EducationTab,
  CertificationsTab,
  AccountabilityTab,
  type DriverData,
} from './driver-profile-tabs';
import { DriverEmploymentTab } from '../resources/users/DriverProfileTabs/DriverEmploymentTab';

const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
  <div hidden={value !== index} style={{ width: '100%' }}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

interface DriverProfileTabsPanelProps {
  tabValue: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  driverData: DriverData | null;
  isLoading?: boolean;
  licenseTypes: any[];
  certificationTypes: any[];
  educationSchools: any[];
  onSaveSnapshot: (data: Partial<DriverData>) => Promise<void>;
  onRefreshDriver: () => void;
  isSticky?: boolean;
}

/**
 * Reusable component for rendering driver profile tabs
 * Shared between StandaloneDriverProfile and DriverProfileTab
 */
export const DriverProfileTabsPanel: React.FC<DriverProfileTabsPanelProps> = ({
  tabValue,
  onTabChange,
  driverData,
  isLoading = false,
  licenseTypes,
  certificationTypes,
  educationSchools,
  onSaveSnapshot,
  onRefreshDriver,
  isSticky = false,
}) => {
  const getRefreshCallback = () => {
    return onRefreshDriver;
  };

  return (
    <Paper sx={{ borderRadius: 2 }}>
      {/* Tabs Navigation */}
      <Tabs
        value={tabValue}
        onChange={onTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          ...(isSticky && {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#fff'
          })
        }}
      >
        <Tab label="Snapshot" icon={<FlagIcon />} iconPosition="start" />
        <Tab label="Licences" icon={<CertIcon />} iconPosition="start" />
        <Tab label="Education" icon={<EducationIcon />} iconPosition="start" />
        <Tab label="Employment" icon={<WorkIcon />} iconPosition="start" />
        <Tab label="Certifications" icon={<CertIcon />} iconPosition="start" />
        <Tab label="Accountability" icon={<FolderIcon />} iconPosition="start" />
      </Tabs>

      {/* Tab Content */}
      <Box sx={{ p: 0 }}>
        {/* Snapshot Tab */}
        <TabPanel value={tabValue} index={0}>
          <SnapshotTab
            driverData={driverData}
            onSave={onSaveSnapshot}
            isLoading={isLoading}
          />
        </TabPanel>

        {/* Licences Tab */}
        <TabPanel value={tabValue} index={1}>
          <LicencesTab
            driverData={driverData}
            licenseTypes={licenseTypes}
            onRefresh={getRefreshCallback()}
          />
        </TabPanel>

        {/* Education Tab */}
        <TabPanel value={tabValue} index={2}>
          <EducationTab
            driverData={driverData}
            educationSchools={educationSchools}
            onRefresh={getRefreshCallback()}
          />
        </TabPanel>

        {/* Employment Tab */}
        <TabPanel value={tabValue} index={3}>
          <DriverEmploymentTab
            driverData={driverData}
            onRefresh={getRefreshCallback()}
          />
        </TabPanel>

        {/* Certifications Tab */}
        <TabPanel value={tabValue} index={4}>
          <CertificationsTab
            driverData={driverData}
            certificationTypes={certificationTypes}
            onRefresh={getRefreshCallback()}
          />
        </TabPanel>

        {/* Accountability Tab */}
        <TabPanel value={tabValue} index={5}>
          <AccountabilityTab
            driverData={driverData}
            onRefresh={getRefreshCallback()}
            isLoading={isLoading}
          />
        </TabPanel>
      </Box>
    </Paper>
  );
};
